import type { components } from "@sagot/api-client";
import { WEBAPP_ASSISTANT_ICON_AVATAR, WEBAPP_CARD_ICON_AUTO, WEBAPP_MAX_STARTERS } from "@sagot/constants";

type Schemas = components["schemas"];
export type WebappSettings = Schemas["WebappSettings"];
export type WebappStudioOut = Schemas["WebappStudioOut"];
export type WebappStudioIn = Schemas["WebappStudioIn"];
export type WebappScope = WebappStudioIn["scope"];

export const WEBAPP_TABS = ["branding", "icons", "content", "layout", "share"] as const;
export type WebappTab = (typeof WEBAPP_TABS)[number];

export function isWebappTab(value: string | undefined): value is WebappTab {
  return WEBAPP_TABS.some((tab) => tab === value);
}

const NONE = { en: "", fil: "" } as const;

/** The built-in design: every value means "same as the widget / as before" (mirror of the API's defaults). */
export const DEFAULT_SETTINGS: WebappSettings = {
  branding: { app_name: "", logo_url: null, avatar_source: "widget", avatar_url: null, favicon_url: null, accent: null, theme: "light", density: "comfortable", radius_px: null },
  icons: { assistant_icon: WEBAPP_ASSISTANT_ICON_AVATAR, card_icon: WEBAPP_CARD_ICON_AUTO, send_style: "icon" },
  content: { title: { ...NONE }, subtitle: { ...NONE }, starters: null, placeholder: { ...NONE }, disclaimer: { ...NONE } },
  layout: { show_history: true, guest_notice: { ...NONE }, powered_by: "widget" },
  share: { enabled: true },
};

export const HEX_COLOR = /^#[\da-f]{6}$/i;

/** Stable text of a design: two designs are the same when these are equal (drives Save / Discard). */
export function fingerprint(settings: WebappSettings): string {
  return JSON.stringify(settings);
}

/** The body of a save. A blank accent or blank starters mean "same as the widget". */
export function toBody(scope: WebappScope, settings: WebappSettings): WebappStudioIn {
  const accent = settings.branding.accent?.trim() ?? "";
  const starters = settings.content.starters?.map((item) => item.trim()).filter((item) => item !== "") ?? null;
  return {
    scope,
    settings: {
      ...settings,
      branding: { ...settings.branding, app_name: settings.branding.app_name.trim(), accent: accent === "" ? null : accent.toLowerCase() },
      content: { ...settings.content, starters },
    },
  };
}

/** What the live preview and the page show as questions: the design's own, else the assistant's starters. */
export function previewStarters(settings: WebappSettings, widgetStarters: readonly string[]): string[] {
  const own = settings.content.starters;
  return (own ?? widgetStarters).filter((item) => item.trim() !== "").slice(0, WEBAPP_MAX_STARTERS);
}

/** The colour the app is drawn in: the design's own when it is a valid hex, else the widget's. */
export function effectiveAccent(settings: WebappSettings, widgetColor: string): string {
  const own = settings.branding.accent?.trim() ?? "";
  return HEX_COLOR.test(own) ? own.toLowerCase() : widgetColor;
}

/** Which picture the preview shows for the assistant, in the web app's order. */
export function previewAvatar(settings: WebappSettings, widgetAvatar: string | null): string | null {
  const { avatar_source: source, avatar_url: custom } = settings.branding;
  if (source === "initials") return null;
  return source === "custom" ? (custom ?? null) : widgetAvatar;
}

export function sameDesign(a: WebappSettings, b: WebappSettings): boolean {
  return fingerprint(toBody("assistant", a).settings) === fingerprint(toBody("assistant", b).settings);
}

/** Move one starter question up or down; out-of-range moves leave the list as it is. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
}
